import geojsonhint from '@mapbox/geojsonhint';
import {
  CLOSED_STATUS, FAILED_STATUS, TERMINATED_STATUS, TreeStates,
} from '../constans/constans';
import storage from './storage';
import getConfig from '../config';

const { projectTimeLeft } = getConfig();
export const nanoToMilli = (timestamp) => Math.floor(timestamp / 1e6);

export const milliToNano = (timestamp) => Math.floor(timestamp * 1e6);

export const convertDurationWithMetrics = (duration, metrics) => {
  const num = +duration;

  // If duration is counted in years
  if (metrics === 'year') {
    const currentDate = new Date();
    const endDate = new Date();
    endDate.setFullYear(currentDate.getFullYear() + num);
    return endDate - currentDate;
  }

  // If duration is counted in days
  // metrics === 'day'
  return num * 24 * 3600 * 1e3;
};

const addZeroAheadToDate = (num) => `${(`0${num.toString()}`).slice(-2)}`;

// Format (00.00.00)
export const formattedDate = (timestamp, divider) => {
  const time = new Date(timestamp);
  return `${addZeroAheadToDate(time.getMonth() + 1)}${divider}${addZeroAheadToDate(time.getDate())}${divider}${time.getFullYear().toString().slice(-2)}`;
};

export const timerCountdown = (timeleft) => {
  const timeleftUpdate = timeleft - projectTimeLeft;
  if (timeleftUpdate >= 0) {
    const days = Math.floor(timeleftUpdate / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeleftUpdate % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeleftUpdate % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeleftUpdate % (1000 * 60)) / 1000);
    return `${days}d ${hours}h ${minutes}m ${seconds}s`;
  }
  return '0d 0h 0m 0s';
};

export const hideEmail = (email) => email.replace(/(.{2})(.*)(?=@)/,
  (gp1, gp2, gp3) => {
    for (let i = 0; i < gp3.length; i++) {
      gp2 += '*';
    } return gp2;
  });

export function extractEmails(text) {
  if (text) {
    return text.match(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi);
  }
  return false;
}
export const filterUniqueProjects = (projects) => {
  const seen = new Set();
  return projects.filter((project) => {
    if (seen.has(project._source.project_id)) {
      return false;
    }
    seen.add(project._source.project_id);
    return true;
  });
};

export const getProjectType = (type, allTypes) => {
  const targetType = allTypes.find((item) => item.value === type);
  return targetType?.title || '';
};

export const getCurrentProjectStage = (stagesArray) => {
  if (stagesArray && stagesArray.length) {
    const multiplier = 1e6;
    const now = Date.now() * multiplier; // milliseconds to nanoseconds
    const currentStageId = stagesArray.findIndex((item) => item.starts_at <= now && now <= item.ends_at);
    return currentStageId;
  }
  return -1;
};

export const getAllZones = (project) => {
  // eslint-disable-next-line camelcase
  const { sampleZones, project_id } = project;
  const allZones = sampleZones.map((zone) => ({
    sample_zone_id: zone.id,
    sample_zone_name: zone.sampleName,
  }));

  return {
    projects: {
      [project_id]: allZones,
    },
  };
};

export const getRemainingZones = (project) => {
  // eslint-disable-next-line camelcase
  const { sampleZones, project_id } = project;
  const targetZones = sampleZones.filter((zone) => {
    if (zone.assign) return false;
    return true;
  });
  const remainingZones = targetZones.map((zone) => ({
    sample_zone_id: zone.id,
    sample_zone_name: zone.sampleName,
  }));

  return {
    projects: {
      [project_id]: remainingZones,
    },
  };
};

export const isProjectOver = (status, lastStageEndTime) => {
  const currentDate = Date.now() * 1e6;
  return status === CLOSED_STATUS
    || status === FAILED_STATUS
    || status === TERMINATED_STATUS
    || (lastStageEndTime > 0 && lastStageEndTime < currentDate);
};

export const checkExistingDataUpload = (allStages, stageId) => {
  if (allStages?.length && stageId >= 0) {
    const targetStage = { ...allStages[stageId] };
    const isDataUploadExist = !!targetStage.data_upload_at;
    return isDataUploadExist;
  }
  return false;
};

export const isValidHttpUrl = (string) => {
  const pattern = new RegExp(
    '^([a-zA-Z]+:\\/\\/)?' // protocol
    + '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' // domain name
    + '((\\d{1,3}\\.){3}\\d{1,3}))' // OR IP (v4) address
    + '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' // port and path
    + '(\\?[;&a-z\\d%_.~+=-]*)?' // query string
    + '(\\#[-a-z\\d_]*)?$', // fragment locator
    'i',
  );
  return pattern.test(string);
};

export const sqmToHa = (squareMeters, decimals = 0) => (squareMeters / 10000).toFixed(decimals);

export const haToSqm = (hectares, decimals = 0) => (hectares * 10000).toFixed(decimals);

export const metersToKilometeres = (value) => value / 1e3;

export const kilometeresToMeters = (value) => value * 1e3;

export const getStageMonitoringData = (data, targetStageId) => {
  const targetStageInfo = data?.stagesInfo?.find((item) => item.stage === +targetStageId);
  const targetSubZone = data?.subZones?.find((item) => item.subId === targetStageInfo?.subId);
  const targetSampleZones = data?.sampleZones?.filter((item) => item.subId === targetSubZone?.subId);
  return { targetStageInfo, targetSubZone, targetSampleZones };
};

export const clearKeyNearLocalStorage = () => {
  try {
    const iterableKeys = Object.keys(localStorage);
    const keysToRemove = iterableKeys.filter((key) => (
      key.includes('near-api-js:keystore')
      || key.includes('undefined_wallet_auth_key')
      || key.includes('_wallet_auth_key')
      || key.includes('near-wallet-selector')
      || key.includes('wallet-selector')
      || key.includes('meteor-wallet')
      || key.includes('hot-connector')
      || key.includes('near-connect')
    ));

    keysToRemove.forEach((key) => storage.del(key));
  } catch (e) {
    // ignore localStorage errors
  }
};

export const getLastStageEndTime = (stages) => (stages?.length ? stages[stages.length - 1].ends_at : -1);

export const hasNonEmptyField = (obj) => {
  // Check if the input is an object
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  // Check if the object has any non-empty fields
  for (const key in obj) {
    // eslint-disable-next-line no-prototype-builtins
    if (obj.hasOwnProperty(key)) {
      const value = obj[key];

      if (Array.isArray(value)) {
        // If the value is an array, recursively check each item in the array
        for (const item of value) {
          if (hasNonEmptyField(item)) {
            return true;
          }
        }
      } else if (typeof value === 'object') {
        // If the value is an object, recursively check the object
        if (hasNonEmptyField(value)) {
          return true;
        }
      } else if (value !== undefined && value !== null && value !== '') {
        // If the value is not an object or array, check if it's non-empty
        return true;
      }
    }
  }

  return false;
};

export const getProjectReference = async (contract, projectId) => {
  try {
    const token = await contract.get_project({ project_id: projectId });

    if (!token) return null;

    const projectCid = token.metadata.reference;
    return projectCid || null;
  } catch (e) {
    console.log(e);
    return null;
  }
};
const checkTreeValidity = (value) => (value[0] === TreeStates.tagLost
  || value[0] === TreeStates.inaccessible
  || value[0] === TreeStates.dead
    || (value[0] === TreeStates.damaged && !!value[3] && !!value[4])
    || (value.includes(TreeStates.alive)
      && (value[1] || !!value[2])
      && (+value[1] >= 1.3 ? !!value[2] : true)
      && !!value[3]
      && !!value[4]));

export const chooseColor = (arr) => {
  if (arr.length > 0) {
    const allIsValues = arr.reduce(
      (prev, cur) => {
        const { treeName, id, ...newCurrent } = cur;
        return prev.concat(Object.values({ ...newCurrent }));
      },
      [],
    );
    if (
      arr.every((el) => {
        const { treeName, id, ...newCurrent } = el;
        return checkTreeValidity(Object.values(newCurrent));
      })
    ) {
      return 'all-data';
    }
    if (allIsValues.some((el) => el)) {
      return 'miss-data';
    }
  }
  return 'no-data';
};

export const validateGeoJSONByRules = (geo) => {
  const isValid = geojsonhint.hint(JSON.stringify(geo), { ignoreRightHandRule: true });
  const isPolygon = geo?.geometry?.type === 'Polygon' || geo?.features?.find((el) => el.geometry.type === 'Polygon');
  const MoreThanOnePolygon = geo?.features?.length > 1;
  if (!isPolygon) {
    return 1;
  } if (MoreThanOnePolygon) {
    return 2;
  } if (isValid.length) {
    return 3;
  }
  return 0;
};

export const makeFileSenderByType = (type = 'general', id, arr) => {
  const formData = new FormData();
  formData.append('project_id', id);
  formData.append('file_type', type);
  arr.map((el) => formData.append('file', el, el.path));
  return formData;
};

export const upNumberString = (input, up = 0.01, needNumber) => {
  const numberMatch = input.match(/^\d+\.\d+/);

  if (numberMatch) {
    const originalNumber = parseFloat(numberMatch[0]);
    const numberString = up.toString();
    const decimalPart = numberString.split('.')[1];
    const decimalLength = decimalPart ? decimalPart.length : 0;
    // Convert the matched string to a float
    const increasedNumber = (originalNumber + up).toFixed(decimalLength); // Increase by 1 and keep two decimal places

    // Replace the original number in the string with the new number
    const updatedString = input.replace(/^\d+\.\d+/, needNumber || increasedNumber);

    return updatedString; // Output: "3.13 How many tree species are you planting in the project zone?"
  }
  return input;
};
export const formatOPN = (opn, decimalPlaces = 2) => {
  const opnExponentiationConstant = 24;
  const bigNumber = BigInt(opn);
  const divisor = BigInt(10) ** BigInt(opnExponentiationConstant);
  const result = Number((bigNumber * BigInt(10) ** BigInt(decimalPlaces)) / divisor)
      / 10 ** decimalPlaces;
  const formattedResult = result.toFixed(decimalPlaces);
  return formattedResult;
};
export const getTotalDataByStage = (stages) => {
  const totalForAllStage = {
    totalValidationPoolRewards: BigInt('0'),
    totalReceived: BigInt('0'),
    totalBufferPoolRewards: BigInt('0'),
    totalEcosystem: BigInt('0'),
  };
  stages.forEach((stage) => {
    totalForAllStage.totalValidationPoolRewards
        += BigInt(stage.project_operator_rewards)
        + BigInt(stage.buffer_pool_rewards)
        + BigInt(stage.validation_pool_rewards)
        + BigInt(stage.planting_pool_rewards);
    totalForAllStage.totalReceived += BigInt(stage.project_operator_rewards);
    totalForAllStage.totalBufferPoolRewards += BigInt(
      stage.buffer_pool_rewards,
    );
    totalForAllStage.totalEcosystem
        += BigInt(stage.validation_pool_rewards)
        + BigInt(stage.planting_pool_rewards);
  });
  const response = {
    dataValidationPoolRewards:
        totalForAllStage.totalValidationPoolRewards.toString(),
    dataReceived: totalForAllStage.totalReceived.toString(),
    dataPoolRewards: totalForAllStage.totalBufferPoolRewards.toString(),
    dataEcosystem: totalForAllStage.totalEcosystem.toString(),
  };
  return response;
};
export const getStartTimeProject = (timestamp) => {
  if (!timestamp) return '';
  const multiplier = 1e6;

  const time = new Date(timestamp / multiplier);
  return time.toLocaleString('default', { month: 'long', year: 'numeric' });
};
export const concatItems = (items) => {
  if (!items) return '';

  if (Array.isArray(items)) {
    if (items.length === 1) return items[0];
    return items.map((type, index) => type.concat(index !== items.length - 1 ? ', ' : ''));
  }

  if (typeof items === 'string') return items;

  return String(items);
};
